import { sx } from '../../lib/sx'

/**
 * The Connect-tab hero, modelled on the real NordVPN app: a map panel with the
 * server pinned on it, the security state written underneath, and the primary
 * connection control.
 *
 * The map is drawn with CSS rather than shipped as an image — at this size a
 * dot matrix reads exactly like the real app's plotted server grid, and it
 * costs nothing to load or to re-theme.
 */
export function ConnectionCard({
  server,
  secured = true,
}: {
  /** e.g. "Lithuania #218" */
  server: string
  secured?: boolean
}) {
  return (
    <div
      style={sx(
        'border-radius:var(--radius-lg);background:var(--bg-secondary);' +
          'border:1px solid var(--border-secondary);overflow:hidden;flex:none'
      )}
    >
      <MapPanel secured={secured} />

      <div style={sx('padding:14px 16px 16px;display:flex;flex-direction:column;gap:14px')}>
        <div style={sx('display:flex;align-items:center;gap:12px')}>
          <span
            style={sx(
              `width:36px;height:36px;flex:none;border-radius:var(--radius-full);` +
                `background:${secured ? 'var(--bg-success-subtle)' : 'var(--bg-critical-subtle)'};` +
                `display:flex;align-items:center;justify-content:center`
            )}
          >
            <span
              style={sx(
                `width:19px;height:19px;` +
                  `background:${secured ? 'var(--text-success)' : 'var(--text-critical)'};` +
                  `-webkit-mask:url(/assets/icons/threat-protection.svg) center/contain no-repeat;` +
                  `mask:url(/assets/icons/threat-protection.svg) center/contain no-repeat`
              )}
            />
          </span>

          <span style={sx('flex:1;display:flex;flex-direction:column;gap:2px;min-width:0')}>
            <span
              style={sx(
                'font-size:var(--fs-md);font-weight:var(--fw-bold);letter-spacing:var(--ls-sm)'
              )}
            >
              {server}
            </span>
            <span
              style={sx(
                `font-size:var(--fs-xs);` +
                  `color:${secured ? 'var(--text-success)' : 'var(--text-critical)'}`
              )}
            >
              {secured ? 'Secured · your traffic is encrypted' : 'Not secured'}
            </span>
          </span>
        </div>

        <span
          style={sx(
            `height:44px;border-radius:var(--radius-full);display:flex;align-items:center;` +
              `justify-content:center;gap:8px;font-size:var(--fs-sm);` +
              `font-weight:var(--fw-medium);` +
              (secured
                ? 'border:1px solid var(--border-primary);color:var(--text-primary)'
                : 'background:var(--bg-accent);color:#fff')
          )}
        >
          {secured ? (
            <>
              <span
                style={sx(
                  'width:16px;height:16px;background:currentColor;' +
                    '-webkit-mask:url(/assets/icons/quick-connect.svg) center/contain no-repeat;' +
                    'mask:url(/assets/icons/quick-connect.svg) center/contain no-repeat'
                )}
              />
              Disconnect
            </>
          ) : (
            'Secure my connection'
          )}
        </span>
      </div>
    </div>
  )
}

/** Dotted server grid with the active node pinned and pulsing at its centre. */
function MapPanel({ secured }: { secured: boolean }) {
  // The pin carries the security state, so it matches the shield below it
  // rather than the brand blue.
  const accent = secured ? 'var(--color-green-500)' : 'var(--bg-critical)'
  const glow = secured ? 'rgba(14,164,100,.20)' : 'rgba(224,47,31,.20)'

  return (
    <div
      style={sx(
        'position:relative;height:152px;' +
          'background-color:#101116;' +
          `background-image:radial-gradient(circle at 50% 50%,${glow} 0%,transparent 58%),` +
          'radial-gradient(rgba(255,255,255,.10) 1px,transparent 1px);' +
          'background-size:100% 100%,13px 13px'
      )}
    >
      <span
        style={sx(
          `position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);` +
            `width:14px;height:14px;border-radius:var(--radius-full);` +
            `background:${accent};border:2px solid rgba(255,255,255,.85);` +
            `animation:${secured ? 'pin-pulse' : 'gring'} 2.4s var(--ease-out) infinite`
        )}
      />

      <span
        style={sx(
          'position:absolute;inset:0;' +
            'background:linear-gradient(180deg,transparent 45%,rgba(29,30,32,.85) 100%)'
        )}
      />
    </div>
  )
}
